/**
 * PupRing Custom JavaScript - Bundle Builder Quick-View Module
 * ============================================================
 * Buy 3 for $99 Bundle Builder - Quick-View Modal Management
 * Split from custom-bundle-ui.js for maintainability
 *
 * Features:
 * - Quick-view modal creation and lifecycle
 * - Product data fetching and rendering
 * - Variant selector with color swatches
 * - Splide carousel initialization
 * - Pet name inputs and validation
 * - Photo uploader integration
 *
 * Dependencies: custom-bundle-ui.js (must load first)
 * Load Order: MUST load AFTER custom-bundle-ui.js
 */

(function() {
  'use strict';

  // Product titles and option values are merchant text: escape before templating into markup or attributes
  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  // Extend BundleUIManager prototype with quick-view methods
  Object.assign(BundleUIManager.prototype, {
    /**
     * Initialize quick-view modal
     */
    initQuickViewModal() {
      // Create modal if it doesn't exist
      if (!document.getElementById(BundleUIManager.QUICK_VIEW_MODAL_ID)) {
        this.createQuickViewModal();
      }

      this.elements.quickViewModal = document.getElementById(BundleUIManager.QUICK_VIEW_MODAL_ID);

      // Phase 1 FIX: Remove previous listener before adding new one
      // Prevents listener accumulation on repeated init() calls
      if (this._boundQuickViewHiddenHandler) {
        this.elements.quickViewModal.removeEventListener('hidden.bs.modal', this._boundQuickViewHiddenHandler);
      }

      // Store bound handler for future cleanup
      this._boundQuickViewHiddenHandler = () => this.cleanupQuickView();
      this.elements.quickViewModal.addEventListener('hidden.bs.modal', this._boundQuickViewHiddenHandler);
    },

    /**
     * Create the quick-view modal element
     */
    createQuickViewModal() {
      const modal = document.createElement('div');
      modal.id = BundleUIManager.QUICK_VIEW_MODAL_ID;
      modal.className = 'modal fade bundle-quick-view-modal';
      modal.setAttribute('tabindex', '-1');
      modal.setAttribute('aria-hidden', 'true');
  
      modal.innerHTML = `
        <div class="modal-dialog modal-lg modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header border-0">
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body" data-quick-view-content>
              <!-- Content populated dynamically -->
            </div>
          </div>
        </div>
      `;
  
      document.body.appendChild(modal);
    },

    /**
     * Open quick-view modal for a product
     */
    async openQuickView(productHandle) {
      const modal = this.elements.quickViewModal;
      const contentContainer = modal.querySelector('[data-quick-view-content]');

      // Only the latest request may render: a slower earlier response (or one arriving after close) is dropped
      const requestToken = (this.quickViewRequestToken || 0) + 1;
      this.quickViewRequestToken = requestToken;
      const isStale = () => requestToken !== this.quickViewRequestToken;
  
      // Show loading state
      contentContainer.innerHTML = this.getLoadingSkeleton();
  
      // Open modal
      const bsModal = bootstrap.Modal.getOrCreateInstance(modal);
      bsModal.show();
  
      try {
        // Fetch product data
        const response = await fetch(`/products/${productHandle}.js`);
        if (isStale()) return;
        if (!response.ok) throw new Error('Product not found');
  
        const product = await response.json();
        if (isStale()) return;
        this.currentQuickViewProduct = product;
  
        // Render content
        contentContainer.innerHTML = this.renderQuickViewContent(product);
  
        // BUG-028 FIX: Safely inject product description after DOM render
        this.injectProductDescription(product);
  
        // Initialize Splide carousel
        this.initQuickViewCarousel();
  
        // Setup variant handlers
        this.initVariantHandlers();
  
        // BUG-020 FIX: Preload all product images for instant variant switching
        this.preloadProductImages(product);
  
        // Initialize quick-view customization handlers (pet names, photo)
        this.initQuickViewPetNameHandlers();
        await this.initQuickViewPhotoUploader(product);
        if (isStale()) return;
        this.updateQuickViewAddButtonState();
  
      } catch (error) {
        if (isStale()) return;
        contentContainer.innerHTML = `
          <div class="text-center py-5">
            <p class="text-danger mb-3">Unable to load product details</p>
            <button class="btn btn-outline-primary" onclick="window.bundleUI.openQuickView('${productHandle}')">
              Try Again
            </button>
          </div>
        `;
      }
    },

    /**
     * Render quick-view modal content
     */
    renderQuickViewContent(product) {
      const state = this.stateManager.get();
      const isInBundle = state.products.some(p => p.productId === product.id);
      const countInBundle = state.products.filter(p => p.productId === product.id).length;
      const isFull = state.products.length >= 3;
  
      // Get first available variant
      const firstAvailable = product.variants.find(v => v.available) || product.variants[0];
  
      // Build button state
      let buttonText = 'Add to Bundle';
      let buttonDisabled = false;
      let buttonClass = 'btn-primary';
  
      if (countInBundle >= 3) {
        buttonText = 'Maximum Added (3)';
        buttonDisabled = true;
        buttonClass = 'btn-secondary';
      } else if (isFull) {
        buttonText = 'Bundle Full (3/3)';
        buttonDisabled = true;
        buttonClass = 'btn-secondary';
      } else if (isInBundle) {
        buttonText = `Add Another (${countInBundle} in bundle)`;
      }
  
      return `
        <div class="row g-4">
          <div class="col-12 col-md-6">
            <div class="bundle-quick-view-gallery">
              <div id="bundle-quick-view-splide" class="splide">
                <div class="splide__track">
                  <ul class="splide__list">
                    ${product.images.map((img, i) => `
                      <li class="splide__slide">
                        <img
                          src="${this.resizeImage(img, '600x600')}"
                          alt="${escapeHtml(product.title)}"
                          class="img-fluid rounded"
                          loading="${i === 0 ? 'eager' : 'lazy'}"
                        >
                      </li>
                    `).join('')}
                  </ul>
                </div>
              </div>
              ${product.images.length > 1 ? `
                <div id="bundle-quick-view-thumbs" class="splide mt-3">
                  <div class="splide__track">
                    <ul class="splide__list">
                      ${product.images.map(img => `
                        <li class="splide__slide">
                          <img
                            src="${this.resizeImage(img, '100x100')}"
                            alt=""
                            class="img-fluid rounded img-thumbnail"
                            loading="lazy"
                          >
                        </li>
                      `).join('')}
                    </ul>
                  </div>
                </div>
              ` : ''}
            </div>
          </div>
          <div class="col-12 col-md-6">
            <h3 class="h4 mb-3">${escapeHtml(product.title)}</h3>
  
            ${this.renderVariantSelector(product, firstAvailable.id)}
  
            <input type="hidden" id="bundle-qv-selected-variant" value="${firstAvailable.id}">
  
            ${this.renderQuickViewCustomization(product, firstAvailable)}
  
            <button
              id="bundle-qv-add-button"
              class="btn ${buttonClass} w-100 mt-4"
              ${buttonDisabled ? 'disabled data-bundle-limit-reached' : ''}
              data-product-id="${product.id}"
              data-product-handle="${product.handle}"
              data-product-title="${escapeHtml(product.title)}"
              data-featured-image="${product.featured_image}"
            >
              ${buttonText}
            </button>
  
            <div id="bundle-qv-description-container" class="bundle-qv-description mt-4 pt-3 border-top" style="display: none;"></div>
          </div>
        </div>
      `;
    },

    /**
     * Safely inject product description into quick-view modal
     * Uses DOM manipulation to avoid template literal parsing issues
     * BUG-028 FIX
     */
    injectProductDescription(product) {
      const container = document.getElementById('bundle-qv-description-container');
      if (!container) return;
  
      // Get description content, checking multiple possible properties
      const description = product.body_html || product.description || '';
  
      // Validate content exists and is meaningful
      const trimmedDescription = description.trim();
      if (!trimmedDescription || trimmedDescription === '<p></p>' || trimmedDescription === '<br>') {
        container.remove();
        return;
      }
  
      // Inject HTML content safely via innerHTML
      container.innerHTML = trimmedDescription;
  
      // Show the container
      container.style.display = '';
    },

    /**
     * Render variant selector buttons
     */
    renderVariantSelector(product, selectedVariantId) {
      if (product.variants.length === 1 && product.variants[0].title === 'Default Title') {
        return ''; // No selector for products with only default variant
      }
  
      // Group by option name
      // BUG-018 FIX: Handle both string and object formats for product.options
      const optionHtml = product.options.map((option, optionIndex) => {
        const optionName = typeof option === 'string' ? option : option.name;
        const optionKey = `option${optionIndex + 1}`;
        const uniqueValues = [...new Set(product.variants.map(v => v[optionKey]))];
  
        // Find currently selected value
        const selectedVariant = product.variants.find(v => v.id === selectedVariantId);
        const selectedValue = selectedVariant ? selectedVariant[optionKey] : uniqueValues[0];
  
        // BUG-034 FIX: Check if this option should be rendered as a color swatch
        const isColorOpt = BundleUIManager.isColorOption(optionName);
        const optionHandle = optionName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  
        if (isColorOpt) {
          // BUG-032 FIX: Render as card-style swatch matching product page
          return `
            <div class="product-option-wrapper bundle-variant-selector mb-3" data-handle="${optionHandle}">
              <h4 class="variant-card-title title d-flex h6 mb-3">
                ${escapeHtml(optionName)}:
                <span class="text-muted ms-2">${escapeHtml(selectedValue)}</span>
              </h4>
              <ul class="variant-card-list variant-card-list--swatch list-unstyled mb-0"
                  data-option-index="${optionIndex}">
                ${uniqueValues.map(value => {
                  const isSelected = value === selectedValue;
                  const isAvailable = product.variants.some(v =>
                    v[optionKey] === value && v.available
                  );
                  const imgUrl = BundleUIManager.getSwatchImageUrl(product, optionIndex, value);
  
                  return `
                    <li class="variant-card-item">
                      <input type="radio"
                             name="bundle-qv-option-${optionIndex}"
                             id="bundle-qv-${optionIndex}-${value.toLowerCase().replace(/[^a-z0-9]+/g, '-')}"
                             class="bundle-variant-input visually-hidden"
                             data-option-value="${escapeHtml(value)}"
                             data-option-index="${optionIndex}"
                             ${isSelected ? 'checked' : ''}
                             ${!isAvailable ? 'disabled' : ''}>
                      <label for="bundle-qv-${optionIndex}-${value.toLowerCase().replace(/[^a-z0-9]+/g, '-')}"
                             class="variant-card-label ${!isAvailable ? 'disabled' : ''}"
                             data-bs-toggle="tooltip"
                             title="${escapeHtml(value)}${!isAvailable ? ' (Sold Out)' : ''}">
                        <img class="variant-card-img"
                             src="${imgUrl}"
                             alt="${escapeHtml(value)}"
                             width="40"
                             height="40"
                             loading="eager"
                             onerror="this.src='data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 40 40%22><rect width=%2240%22 height=%2240%22 fill=%22%23f0f0f0%22 rx=%2220%22/></svg>'">
                      </label>
                    </li>
                  `;
                }).join('')}
              </ul>
            </div>
          `;
        } else {
          // BUG-032 FIX: Text options also use card style (Ring Size, etc.)
          return `
            <div class="product-option-wrapper bundle-variant-selector mb-3" data-handle="${optionHandle}">
              <h4 class="variant-card-title title d-flex h6 mb-3">
                ${escapeHtml(optionName)}:
                <span class="text-muted ms-2">${escapeHtml(selectedValue)}</span>
              </h4>
              <ul class="variant-card-list list-unstyled mb-0" data-option-index="${optionIndex}">
                ${uniqueValues.map(value => {
                  const isSelected = value === selectedValue;
                  const isAvailable = product.variants.some(v =>
                    v[optionKey] === value && v.available
                  );
  
                  return `
                    <li class="variant-card-item">
                      <input type="radio"
                             name="bundle-qv-option-${optionIndex}"
                             id="bundle-qv-${optionIndex}-${value.toLowerCase().replace(/[^a-z0-9]+/g, '-')}"
                             class="bundle-variant-input visually-hidden"
                             data-option-value="${escapeHtml(value)}"
                             data-option-index="${optionIndex}"
                             ${isSelected ? 'checked' : ''}
                             ${!isAvailable ? 'disabled' : ''}>
                      <label for="bundle-qv-${optionIndex}-${value.toLowerCase().replace(/[^a-z0-9]+/g, '-')}"
                             class="variant-card-label ${!isAvailable ? 'disabled' : ''}">
                        ${escapeHtml(value)}${!isAvailable ? ' (Sold Out)' : ''}
                      </label>
                    </li>
                  `;
                }).join('')}
              </ul>
            </div>
          `;
        }
      }).join('');

      return optionHtml;
    },

    /**
     * Initialize Splide carousel in quick-view
     */
    initQuickViewCarousel() {
      if (typeof Splide === 'undefined') return;
  
      // Destroy existing instances
      if (this.quickViewSplide) {
        this.quickViewSplide.destroy();
        this.quickViewSplide = null;
      }
      if (this.quickViewThumbsSplide) {
        this.quickViewThumbsSplide.destroy();
        this.quickViewThumbsSplide = null;
      }
  
      const mainEl = document.getElementById('bundle-quick-view-splide');
      const thumbsEl = document.getElementById('bundle-quick-view-thumbs');
  
      if (!mainEl) return;
  
      if (thumbsEl) {
        // Main + Thumbnails sync
        this.quickViewThumbsSplide = new Splide(thumbsEl, {
          rewind: true,
          fixedWidth: 80,
          fixedHeight: 80,
          gap: 8,
          isNavigation: true,
          pagination: false,
          arrows: false
        });
  
        this.quickViewSplide = new Splide(mainEl, {
          type: 'fade',
          rewind: true,
          pagination: false,
          arrows: true,
          speed: 250  // BUG-020 FIX: Reduce from default 400ms for snappier transitions
        });
  
        this.quickViewSplide.sync(this.quickViewThumbsSplide);
        this.quickViewSplide.mount();
        this.quickViewThumbsSplide.mount();
      } else {
        // Main only
        this.quickViewSplide = new Splide(mainEl, {
          type: 'fade',
          rewind: true,
          pagination: true,
          arrows: true,
          speed: 250  // BUG-020 FIX: Reduce from default 400ms for snappier transitions
        });
        this.quickViewSplide.mount();
      }
    },

    /**
     * Initialize variant button handlers
     * Uses event delegation on modal to prevent listener accumulation
     */
    initVariantHandlers() {
      const modal = this.elements.quickViewModal;
      const product = this.currentQuickViewProduct;
      if (!modal || !product) return;
  
      // Use event delegation - only set up once per modal lifetime
      if (!modal._bundleVariantDelegationInitialized) {
        this.boundModalClickHandler = (e) => {
          // BUG-032 FIX: Handle variant card label clicks (new card-style selectors)
          const cardLabel = e.target.closest('.variant-card-label');
          if (cardLabel && !cardLabel.classList.contains('disabled')) {
            // The label's 'for' attribute links to the radio input
            // Clicking label auto-checks the input, but we also update UI
            const input = cardLabel.previousElementSibling;
            if (input && input.classList.contains('bundle-variant-input')) {
              input.checked = true;
  
              // Update the title span with selected value
              const wrapper = cardLabel.closest('.product-option-wrapper');
              if (wrapper) {
                const titleSpan = wrapper.querySelector('.variant-card-title span.text-muted');
                if (titleSpan) {
                  titleSpan.textContent = input.dataset.optionValue;
                }
              }
  
              this.updateSelectedVariant();
            }
            return;
          }
  
          // Handle add button clicks
          if (e.target.closest('#bundle-qv-add-button')) {
            this.handleAddFromQuickView();
          }
        };
        modal.addEventListener('click', this.boundModalClickHandler);
        modal._bundleVariantDelegationInitialized = true;
      }
    },

    /**
     * Update selected variant based on current option selections
     */
    updateSelectedVariant() {
      const modal = this.elements.quickViewModal;
      const product = this.currentQuickViewProduct;
      if (!product) return;
  
      // BUG-032 FIX: All variant cards now use radio inputs
      const selectedOptions = [];
      modal.querySelectorAll('[data-option-index]').forEach(container => {
        const selectedInput = container.querySelector('.bundle-variant-input:checked');
        if (selectedInput) {
          selectedOptions.push(selectedInput.dataset.optionValue);
        }
      });
  
      // Find matching variant
      const matchingVariant = product.variants.find(v => {
        return selectedOptions.every((opt, i) => v[`option${i + 1}`] === opt);
      });
  
      if (matchingVariant) {
        // Update hidden input
        modal.querySelector('#bundle-qv-selected-variant').value = matchingVariant.id;
  
        // Update image if variant has one
        if (matchingVariant.featured_image && this.quickViewSplide) {
          const imageSrc = matchingVariant.featured_image.src;
          const imageFilename = imageSrc.split('?')[0].split('/').pop();
  
          const imageIndex = product.images.findIndex(img => img.includes(imageFilename));
          if (imageIndex >= 0) {
            this.quickViewSplide.go(imageIndex);
          }
        }
  
        // Re-render customization section if pendant count changed
        this.handleVariantChangeForCustomization(product, matchingVariant);
      } else {
        // No variant exists for this combination: drop the previous id so it cannot be added
        modal.querySelector('#bundle-qv-selected-variant').value = '';
        this.updateQuickViewAddButtonState();
      }
    },

    /**
     * Handle variant change - re-render customization if pendant count changed
     */
    handleVariantChangeForCustomization(product, newVariant) {
      const oldRequirements = this.currentQuickViewRequirements;
      const newRequirements = BundleStateManager.parseProductRequirements(product, newVariant);
  
      // Only re-render if pendant count changed (e.g., "1 Pendant" → "2 Pendants")
      if (oldRequirements?.pendantCount !== newRequirements.pendantCount) {
        // Preserve existing pet names
        const existingPetNames = [];
        document.querySelectorAll('.bundle-qv-petname-input').forEach(input => {
          existingPetNames.push(input.value);
        });
  
        // Re-render customization section
        const customizationContainer = document.querySelector('.bundle-qv-customization');
        if (customizationContainer) {
          customizationContainer.outerHTML = this.renderQuickViewCustomization(product, newVariant);
  
          // Restore pet names (as many as fit the new count)
          const newInputs = document.querySelectorAll('.bundle-qv-petname-input');
          existingPetNames.forEach((name, i) => {
            if (newInputs[i]) {
              newInputs[i].value = name;
              // Update char counter
              const counter = newInputs[i].parentElement.querySelector('.bundle-qv-char-count');
              if (counter) {
                const maxLen = parseInt(newInputs[i].getAttribute('maxlength'), 10);
                counter.textContent = `${name.length}/${maxLen}`;
              }
            }
          });
  
          // Re-init handlers
          this.initQuickViewPetNameHandlers();
  
          // Re-init photo uploader if product requires photo (BUG FIX: was missing)
          if (newRequirements.requiresPhoto) {
            this.initQuickViewPhotoUploader(product);
          }
        } else if (newRequirements.requiresPetName || newRequirements.requiresPhoto) {
          // No customization section exists but new variant needs one - insert it
          const variantInput = document.getElementById('bundle-qv-selected-variant');
          if (variantInput) {
            variantInput.insertAdjacentHTML('afterend', this.renderQuickViewCustomization(product, newVariant));
            this.initQuickViewPetNameHandlers();
            this.initQuickViewPhotoUploader(product);
          }
        }
      }
  
      // Always update button state
      this.updateQuickViewAddButtonState();
    },

    /**
     * Handle add from quick-view modal
     */
    handleAddFromQuickView() {
      if (this.isProcessing) return;
      this.isProcessing = true;
  
      try {
        const modal = this.elements.quickViewModal;
        const product = this.currentQuickViewProduct;
        const addButton = modal.querySelector('#bundle-qv-add-button');
  
        if (!product || !addButton) return;
  
        // Validate customization FIRST (before checking bundle state)
        const validation = this.validateQuickViewCustomization();
        if (!validation.isValid) {
          this.showToast(validation.errors[0], 'error');
          return;
        }
  
        const state = this.stateManager.get();
  
        // Re-check bundle size
        if (state.products.length >= 3) {
          this.showToast("Bundle is full. Remove a product first.", 'warning');
          return;
        }
  
        // Get selected variant
        const variantId = parseInt(modal.querySelector('#bundle-qv-selected-variant').value, 10);
        const variant = product.variants.find(v => v.id === variantId);
  
        if (!variant) {
          this.showToast("Please select a variant.", 'error');
          return;
        }
  
        if (!variant.available) {
          this.showToast("This variant is sold out.", 'error');
          return;
        }
  
        // BUG-069 FIX: Parse product tags to get requirements
        // Quick-view has full product JSON, so we can parse tags immediately
        const requirements = BundleStateManager.parseProductRequirements(product, variant);
  
        // Collect pet names from inputs
        const petNames = [];
        const petNameInputs = modal.querySelectorAll('.bundle-qv-petname-input');
        petNameInputs.forEach(input => {
          if (input.value.trim()) {
            petNames.push(input.value.trim());
          }
        });
  
        // Get photo URL from pending upload
        const photoUrl = this.pendingQuickViewPhoto || null;
  
        // Add to state with parsed requirements AND customization data
        const success = this.stateManager.addProduct({
          productId: product.id,
          productHandle: product.handle,
          productTitle: product.title,
          featuredImage: product.featured_image,
          selectedVariantId: variant.id,
          selectedVariantTitle: variant.title,
          selectedVariantImage: variant.featured_image?.src || product.featured_image,
          // BUG-069 FIX: Include parsed requirements from tags
          charLimit: requirements.charLimit,
          requiresPhoto: requirements.requiresPhoto,
          requiresPetName: requirements.requiresPetName,
          noCaps: requirements.noCaps,
          pendantCount: requirements.pendantCount,
          // Pre-filled customization data from quick-view
          customization: {
            petNames: petNames,
            photoUrl: photoUrl,
            engraving: {}  // Engraving is done in Step 2 only
          }
        });
  
        if (success) {
          // Clean up photo uploader reference
          if (this.quickViewPhotoUploader) {
            this.quickViewPhotoUploader.cleanup();
            this.quickViewPhotoUploader = null;
          }
          this.pendingQuickViewPhoto = null;
  
          // Close modal
          bootstrap.Modal.getInstance(modal).hide();
  
          // Show success toast
          this.showToast(`${product.title} added to bundle!`, 'success');
        }
      } finally {
        this.isProcessing = false;
      }
    },

    /**
     * Cleanup when quick-view closes
     */
    cleanupQuickView() {
      if (this.quickViewSplide) {
        this.quickViewSplide.destroy();
        this.quickViewSplide = null;
      }
      if (this.quickViewThumbsSplide) {
        this.quickViewThumbsSplide.destroy();
        this.quickViewThumbsSplide = null;
      }
      // Cleanup photo uploader
      if (this.quickViewPhotoUploader) {
        this.quickViewPhotoUploader.cleanup();
        this.quickViewPhotoUploader = null;
      }
      // Reset customization state
      this.pendingQuickViewPhoto = null;
      this.currentQuickViewRequirements = null;
      this.currentQuickViewProduct = null;
      // Invalidate any quick-view request still in flight
      this.quickViewRequestToken = (this.quickViewRequestToken || 0) + 1;
    },

    // ═══════════════════════════════════════════════════════════════
    // QUICK-VIEW CUSTOMIZATION (Pet Names + Photo Upload)
    // ═══════════════════════════════════════════════════════════════

    /**
     * Render customization section for quick-view modal
     * Shows pet name inputs and/or photo upload based on product requirements
     */
    renderQuickViewCustomization(product, variant) {
      const requirements = BundleStateManager.parseProductRequirements(product, variant);
  
      // Store requirements for validation
      this.currentQuickViewRequirements = requirements;
  
      // Only show section if product has requirements
      if (!requirements.requiresPetName && !requirements.requiresPhoto) {
        return '';
      }
  
      let html = '<div class="bundle-qv-customization mt-4 pt-4 border-top">';
      html += '<h5 class="h6 mb-3">Customize Your Product</h5>';
  
      // Pet name inputs
      if (requirements.requiresPetName) {
        html += this.renderQuickViewPetNameInputs(
          requirements.pendantCount,
          requirements.charLimit,
          requirements.noCaps
        );
      }
  
      // Photo upload
      if (requirements.requiresPhoto) {
        html += this.renderQuickViewPhotoUpload();
      }
  
      html += '</div>';

      return html;
    },

    /**
     * Render pet name input(s) for quick-view modal
     */
    renderQuickViewPetNameInputs(pendantCount, charLimit, noCaps) {
      const count = pendantCount || 1;
      let html = '<div class="bundle-qv-petnames mb-3">';
  
      for (let i = 0; i < count; i++) {
        const label = count === 1 ? "Pet's Name" : `Pet's Name ${i + 1}`;
        const inputId = `bundle-qv-petname-${i}`;
  
        html += `
          <div class="mb-2">
            <label for="${inputId}" class="form-label small mb-1">
              ${label} <span class="text-danger">*</span>
            </label>
            <div class="position-relative">
              <input
                type="text"
                class="form-control bundle-qv-petname-input"
                id="${inputId}"
                data-petname-index="${i}"
                data-no-caps="${noCaps ? 'true' : 'false'}"
                maxlength="${charLimit}"
                placeholder="Enter name (max ${charLimit} chars)"
                required
              >
              <small class="position-absolute end-0 top-50 translate-middle-y me-2 text-muted bundle-qv-char-count">
                0/${charLimit}
              </small>
            </div>
          </div>
        `;
      }
  
      html += '</div>';
      return html;
    },

    /**
     * Render photo upload container for quick-view modal
     */
    renderQuickViewPhotoUpload() {
      return `
        <div class="bundle-qv-photo mb-3">
          <label class="form-label small mb-1">
            Pet Photo <span class="text-danger">*</span>
          </label>
          <div id="bundle-qv-photo-container" class="bundle-qv-photo-wrapper">
            <!-- Photo uploader will be initialized here -->
            <div class="bundle-qv-photo-placeholder text-center p-3 border rounded bg-light">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="text-muted mb-2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <p class="small text-muted mb-0">Loading uploader...</p>
            </div>
          </div>
        </div>
      `;
    },

    /**
     * Initialize pet name input handlers for quick-view modal
     * Phase 1 FIX: Use event delegation to prevent listener accumulation
     */
    initQuickViewPetNameHandlers() {
      const modal = this.elements.quickViewModal;
      if (!modal) return;

      // Phase 1 FIX: Single delegated handler on modal instead of per-input handlers
      // This prevents listener accumulation when inputs are re-rendered on variant change
      if (modal._hasQvPetNameDelegate) return; // Already set up

      modal.addEventListener('input', (e) => {
        // Only handle pet name inputs
        if (!e.target.matches('.bundle-qv-petname-input')) return;

        const input = e.target;
        const noCaps = input.dataset.noCaps === 'true';
        const maxLen = parseInt(input.getAttribute('maxlength'), 10) || 8;
        const counter = input.parentElement?.querySelector('.bundle-qv-char-count');

        let value = input.value;

        // Transform: UPPERCASE or Title Case
        if (noCaps) {
          // Title case: first letter uppercase, rest lowercase
          value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
        } else {
          // UPPERCASE (default)
          value = value.toUpperCase();
        }

        // Enforce char limit
        if (value.length > maxLen) {
          value = value.substring(0, maxLen);
        }

        input.value = value;

        // Update counter
        if (counter) {
          counter.textContent = `${value.length}/${maxLen}`;
        }

        // Update button state
        this.updateQuickViewAddButtonState();
      });

      modal._hasQvPetNameDelegate = true;
    },

    /**
     * Initialize photo uploader in quick-view modal
     */
    async initQuickViewPhotoUploader(product) {
      if (!this.currentQuickViewRequirements?.requiresPhoto) return;
  
      const container = document.getElementById('bundle-qv-photo-container');
      if (!container) return;
  
      // Reset pending photo
      this.pendingQuickViewPhoto = null;
  
      // Use existing BundlePhotoUploader class
      if (!window.BundlePhotoUploader) {
        console.warn('[BundleUI] BundlePhotoUploader not loaded');
        container.innerHTML = `
          <div class="alert alert-warning small mb-0">
            Photo upload not available. Please refresh the page.
          </div>
        `;
        return;
      }
  
      // Create uploader instance for quick-view context
      this.quickViewPhotoUploader = new BundlePhotoUploader(this.stateManager);
  
      // Initialize with callback to store URL
      await this.quickViewPhotoUploader.initForQuickView(container, (photoUrl) => {
        this.pendingQuickViewPhoto = photoUrl;
        this.updateQuickViewAddButtonState();
      });
    },

    /**
     * Update "Add to Bundle" button state based on validation
     */
    updateQuickViewAddButtonState() {
      const button = document.getElementById('bundle-qv-add-button');
      // Bundle full / per-product maximum: keep the button as rendered
      if (!button || button.hasAttribute('data-bundle-limit-reached')) return;
  
      const variantInput = document.getElementById('bundle-qv-selected-variant');
      const hasVariant = Boolean(variantInput?.value);
      const requirements = this.currentQuickViewRequirements;
      const needsCustomization = Boolean(requirements?.requiresPetName || requirements?.requiresPhoto);
      const isValid = hasVariant && (!needsCustomization || this.validateQuickViewCustomization().isValid);
  
      button.disabled = !isValid;
      button.classList.toggle('btn-primary', isValid);
      button.classList.toggle('btn-secondary', !isValid);
    },

    /**
     * Validate all required customization fields
     */
    validateQuickViewCustomization() {
      const requirements = this.currentQuickViewRequirements;
      const errors = [];
  
      // No requirements = always valid
      if (!requirements) {
        return { isValid: true, errors: [] };
      }
  
      // Check pet names
      if (requirements.requiresPetName) {
        const inputs = document.querySelectorAll('.bundle-qv-petname-input');
        const pendantCount = requirements.pendantCount || 1;
  
        for (let i = 0; i < pendantCount; i++) {
          const input = inputs[i];
          if (!input || !input.value.trim()) {
            const label = pendantCount > 1 ? ` ${i + 1}` : '';
            errors.push(`Pet name${label} is required`);
          }
        }
      }
  
      // Check photo
      if (requirements.requiresPhoto) {
        if (!this.pendingQuickViewPhoto) {
          errors.push('Photo is required');
        }
      }
  
      return {
        isValid: errors.length === 0,
        errors
      };
    }
  });
})();

// Export for other modules
window.BundleUIQuickViewLoaded = true;
